import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CustomHeader from "../../components/header";
import PageHeader from "../../components/pageHeader";
import mulesoftLogo from "../../assets/icons/mulesoft.png";
import './accessmanagement.scss';
import { Accordion, Card, Container, Modal, Button } from 'react-bootstrap';
import socketIOClient from "socket.io-client";
import {
    orgList, azureList, azureAccountList, githubList, gitlabList,
    jenkinsList, muleEnvList, grafanaList, bitbucketList, awsList,
    awsAccountList, gkeList, flexGatewayList, VMEC2List,
    registerFlexGatewayList, sonarList, rtfRequestList, elkList, confluenceList, azureVaultList, standardsList
} from "../../constants/configurationPageConstants";
import { anypoint, domain, ENDPOINT } from "../../config.js";
import AddNewResource from "../common/addnewresourcemodal";
import AddActiveMuleContent from "../common/addactivemulecontent";
import useConfirm from "../../hooks/useconfirm";
import { TableBodyforGke } from "../../components/tableComponent/index";
import { NoSettingsFound } from "../../utilities";

// Official brand marks via the Simple Icons CDN (brand colors; light where the default is
// too dark for the card). Falls back to the emoji icon if a logo fails to load.
const BRAND_LOGOS = {
    anypoint:  mulesoftLogo, // bundled asset — Simple Icons dropped the mulesoft slug
    github:    'https://cdn.simpleicons.org/github/ffffff',
    gitlab:    'https://cdn.simpleicons.org/gitlab',
    azure:     'https://cdn.simpleicons.org/microsoftazure',
    jenkins:   'https://cdn.simpleicons.org/jenkins',
    aws:       'https://cdn.simpleicons.org/amazonwebservices/FF9900',
    gke:       'https://cdn.simpleicons.org/googlecloud',
    bitbucket: 'https://cdn.simpleicons.org/bitbucket',
    sonarqube: 'https://cdn.simpleicons.org/sonarqube',
    confluence: 'https://cdn.simpleicons.org/confluence/2684FF',
};

// ==========================================
// INTEGRATION CARD DEFINITIONS
// ==========================================
const integrationDefinitions = [
    {
        id: 'anypoint', name: 'Anypoint', icon: '🔌',
        description: 'Connect MuleSoft Anypoint Platform for API management and integration deployments',
        stateKey: 'muleOrg', color: '#00A0DF', modalType: 'muleorg', activePage: 'Anypoint', resourceType: 'mule/org',
        guide: {
            title: 'How to configure Anypoint',
            steps: [
                'Log in to Anypoint Platform (anypoint.mulesoft.com).',
                'In INTELOG, click "Add Mule Org" and enter your organisation name exactly as shown in Anypoint → Access Management → Business Groups.',
                'After saving the org, expand it and click "Add Mule Settings".',
                'Business Group ClientId & Secret: go to Anypoint → Access Management → Business Groups → select your org → Settings tab. Copy the Client ID and Client Secret (click the dots to reveal the secret).',
                'ConnectedApp Client Id & Secret: go to Anypoint → Access Management → Connected Apps → Create app. Select "App acts on its own behalf", grant scopes: Design Center, Exchange Viewer, Runtime Manager. Copy the generated Client ID and Secret — the Secret is shown only once.',
                'Paste all four values into the Mule Settings form and save.',
            ],
        },
    },
    {
        id: 'github', name: 'GitHub', icon: '📦',
        description: 'Integrate your GitHub repositories for AI-powered code reviews, deployments, and intelligent workflows',
        stateKey: 'github', color: '#181717', modalType: 'github', activePage: 'Github', resourceType: 'repo',
        guide: {
            title: 'How to get a GitHub token',
            steps: [
                'Go to GitHub → Settings → Developer Settings → Personal access tokens.',
                'Click "Generate new token (classic)".',
                'Give it a name and select scopes: repo, workflow, read:org.',
                'Click "Generate token" and copy it immediately — it is shown only once.',
                'Paste the token in the configuration form here.',
            ],
        },
    },
    {
        id: 'gitlab', name: 'GitLab', icon: '🦊',
        description: 'Connect GitLab repositories to enable AI code reviews and automated workflows',
        stateKey: 'gitlab', color: '#FC6D26', modalType: 'gitlab', activePage: 'GitLab', resourceType: 'gitlab',
        guide: {
            title: 'How to get a GitLab token',
            steps: [
                'Log in to GitLab and go to User Settings → Access Tokens.',
                'Click "Add new token" and give it a name.',
                'Select scopes: api, read_repository, write_repository.',
                'Set an expiry date and click "Create personal access token".',
                'Copy the token — it is shown only once.',
                'Paste the token in the configuration form here.',
            ],
        },
    },
    {
        id: 'azure', name: 'Azure', icon: '☁️',
        description: 'Integrate Azure DevOps and AKS clusters for cloud deployments',
        stateKey: 'azaccount', color: '#0078D4', modalType: 'azaccount', activePage: 'Azure', resourceType: 'azaccount',
        guide: {
            title: 'How to get Azure credentials',
            steps: [
                'Go to Azure Portal → Microsoft Entra ID → App registrations.',
                'Click "New registration", give it a name, and register.',
                'Note the Application (Client) ID and Directory (Tenant) ID.',
                'Under Certificates & Secrets, create a new Client Secret and copy its value.',
                'In your subscription → IAM, assign the app the Contributor role.',
                'Paste Client ID, Tenant ID, and Client Secret in the form here.',
            ],
        },
    },
    {
        id: 'jenkins', name: 'Jenkins', icon: '🔧',
        description: 'Create PRs, debug code, ask questions about your repos directly from Jenkins',
        stateKey: 'jenkins', color: '#D24939', modalType: 'jenkins', activePage: 'Jenkins', resourceType: 'jenkins',
        guide: {
            title: 'How to get a Jenkins API token',
            steps: [
                'Log in to Jenkins and click your username in the top-right corner.',
                'Go to Configure (or "User Settings").',
                'Scroll to the API Token section and click "Add new Token".',
                'Name the token and click "Generate".',
                'Copy the token — it is shown only once.',
                'Provide your Jenkins URL, username, and this token in the form here.',
            ],
        },
    },
    /* AWS (key-based) connected-app is retired in favour of keyless onboarding on the
       Cloud Accounts page (/settings/cloud-accounts/aws). Kept here commented for reference.
    {
        id: 'aws', name: 'AWS', icon: '🟠',
        description: 'Connect AWS services for cloud infrastructure and deployments',
        stateKey: 'awsaccount', color: '#FF9900', modalType: 'awsAccount', activePage: 'AWS', resourceType: 'awsaccount',
        guide: {
            title: 'How to get AWS access keys',
            steps: [
                'Log in to AWS Console and go to IAM → Users.',
                'Select your user (or create a dedicated one) and open the Security credentials tab.',
                'Click "Create access key" and select the appropriate use case.',
                'Copy the Access Key ID and Secret Access Key.',
                'Ensure the IAM user has the required permissions (e.g. ECS, EKS, or EC2 policies).',
                'Paste both keys in the configuration form here.',
            ],
        },
    },
    */
    {
        id: 'gke', name: 'GKE', icon: '☸️',
        description: 'Integrate Google Kubernetes Engine for container orchestration',
        stateKey: 'gkeresource', color: '#4285F4', modalType: 'addgkeaccount', activePage: 'GKE', resourceType: 'gkeresource',
        guide: {
            title: 'How to get GKE credentials',
            steps: [
                'Go to Google Cloud Console → IAM & Admin → Service Accounts.',
                'Click "Create Service Account", give it a name, and assign the role "Kubernetes Engine Admin".',
                'Open the service account, go to the Keys tab, and click "Add Key → Create new key".',
                'Select JSON format and download the key file.',
                'Note your GCP Project ID and the cluster region from GKE → Clusters.',
                'Upload or paste the JSON key content in the configuration form here.',
            ],
        },
    },
    {
        id: 'bitbucket', name: 'Bitbucket', icon: '📘',
        description: 'Integrate Bitbucket repositories for intelligent code analysis and automation',
        stateKey: 'bitbucket', color: '#0052CC', modalType: 'bitbucket', activePage: 'Bitbucket', resourceType: 'bitbucket',
        guide: {
            title: 'How to get a Bitbucket app password',
            steps: [
                'Log in to Bitbucket and go to Personal Settings → App passwords.',
                'Click "Create app password" and give it a label.',
                'Select permissions: Repositories (Read, Write) and Pull Requests (Read, Write).',
                'Click "Create" and copy the generated password immediately.',
                'Use your Bitbucket username and this app password in the form here.',
            ],
        },
    },
    {
        id: 'sonarqube', name: 'SonarQube', icon: '🔍',
        description: 'Connect SonarQube for code quality analysis and security scanning',
        stateKey: 'sonarqube', color: '#4E9BCD', modalType: 'sonarqube', activePage: 'Sonarqube', resourceType: 'sonarqube',
        guide: {
            title: 'How to get a SonarQube token',
            steps: [
                'Log in to your SonarQube instance and click your avatar → My Account.',
                'Go to the Security tab and find "Generate Tokens".',
                'Enter a token name and click "Generate".',
                'Copy the token — it is shown only once.',
                'Provide your SonarQube server URL and this token in the form here.',
            ],
        },
    },
    {
        id: 'confluence', name: 'Confluence', icon: '📝',
        description: 'Integrate Confluence for documentation generation and knowledge management',
        stateKey: 'confluence', color: '#172B4D', modalType: 'confluence', activePage: 'Confluence', resourceType: 'confluence',
        guide: {
            title: 'How to get a Confluence API token',
            steps: [
                'Go to id.atlassian.com →Account Settings → Security → API tokens.',
                'Click "Create API token", give it a label, and click "Create".',
                'Copy the token — it is shown only once.',
                'Use your Atlassian account email as the username.',
                'Provide your Confluence base URL (e.g. https://yourorg.atlassian.net) in the form here.',
            ],
        },
    },
    {
        id: 'standards', name: 'Compliance Standards', icon: '📋',
        description: 'Save organisation-wide best practice documents. Once saved, any team member can select them directly in the Compliance & Enhancement Agent — no re-uploading needed.',
        stateKey: 'standards', color: '#00c98d', modalType: 'standards', activePage: 'Standards', resourceType: 'standards',
        guide: {
            title: 'How to add a compliance standard',
            steps: [
                'Click "+ Add More" to open the standard form.',
                'Give the standard a name (e.g. "MuleSoft Best Practices v2").',
                'Choose the source type: Upload a PDF/Word/text document, or paste text directly.',
                'Save — the standard is immediately available as an option in the Compliance & Enhancement Agent.',
            ],
        },
    },
];

function Accessmanagement(props) {
    const navigate = useNavigate();
    const [file, setFile] = useState([]);
    const { confirm } = useConfirm();
    const [openGuide, setOpenGuide] = useState(null); // { id, top, right }
    const [showPageHelp, setShowPageHelp] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [editItem,   setEditItem]   = useState(null);   // the config row being edited
    const [isEditMode, setIsEditMode] = useState(false);

    useEffect(() => {
        if (!openGuide) return;
        const handler = (e) => {
            if (!e.target.closest('.ca-guide-popover') && !e.target.closest('.ca-guide-btn')) {
                setOpenGuide(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [openGuide]);

    const [state, setState] = useState({
        username: "", password: "", azusername: "", azpassword: "",
        organization: "", location: "", resource_group_name: "",
        containerRepository: "", azure_container_registry_name: "", aks_cluster_name: "",
        open: false, resourcesAvailable: false,
        resource: [], azaccount: [], grafana: [], github: [], gitlab: [],
        jenkins: [], mule: [], muleOrg: [], sonarqube: [], elk: [],
        ec2List: [], flexGateways: [], registeredGateways: [],
        gkeresource: [], gkecluster: [], bitbucket: [],
        awsresources: [], awsaccount: [], confluence: [], rtfData: [], azureVault: [], standards: [],
        activePage: '', activeModalType: '', selectedIntegration: null,
        gitUsername: '', gitPat: '', newOrg: '', newGkeAcc: '', gitRepo: '',
        eeMavenUsername: '', eeMavenPassword: '',
        standardName: '', standardType: 'document', standardContent: '',
        standardsValidation: {},
        muleOrganization: '', activeMuleContent: 'VPC',
        selectedMuleOrg: '', selectedMuleUsername: '', selectedMulePassword: '',
        selectedMuleOrgId: '', muleSettingsId: '',
        sonarServer: '', sonarUsername: '', sonarPassword: '', sonarConnectionName: '',
        rtfName: '', elkIP: '', elkUsername: '', elkPassword: '',
        confluenceEmail: '', confluencePat: '', confluenceBaseUrl: '',
        subscriptionId: '', subscriptionName: '', tenantId: '', serviceprincipalid: '', serviceprincipalkey: '', keyVaultName: '',
        organizationEnvironment: ["dev", "prod", "test"],
        deployTargetEnv: '', targetCluster: '',
        azureValidation: {}, jenkinsValidation: {}, sonarValidation: {},
        muleValidation: {}, grafanaValidation: {}, awsValidation: {},
        bitbucketValidation: {}, githubValidation: {}, gitlabValidation: {},
        elkValidation: {}, confluenceValidation: {}, rtfValidation: {}, azureVaultValidation: {},
        muleOrgValidation: {}, vmEc2Validation: {}, registerFlexGatewayValidation: {},
        flexGatewayValidation: {}, gkeValidation: {},
        azureResourceCreating: false, gkeResourceCreating: false,
        awsResourceCreating: false, rtfResourceCreating: false,
        muleOrgNotDelete: false, showMuleSettings: false,
        showModal: false, showDetailsModal: false, showExpandedModal: false,
        expandedIntegration: null,
        confirm: false, validationFailed: false, validationError: '',
        id: '', index: '', stateVar: '', url: '', apiPathLength: 0,
        eks_environment: 'production', eks_vpc_cidr: '10.2.0.0/16',
        eks_node_instance_type: 't3.medium', eks_node_min_size: 1,
        eks_node_max_size: 5, eks_node_desired_size: 1, eks_node_volume_size: 50,
    });

    // ==========================================
    // HELPER FUNCTIONS
    // ==========================================

    const onChange = (event) => {
        const { name, value, checked, type } = event.target;
        type === "checkbox"
            ? setState(prev => ({ ...prev, [name]: checked }))
            : setState(prev => ({ ...prev, [name]: value }));
    };

    const handleChange = e => {
        const fileReader = new FileReader();
        fileReader.readAsText(e.target.files[0], "UTF-8");
        fileReader.onload = e => setFile(e.target.result);
    };

    const gotoPage = (url) => navigate(url);

    const getResource = (item, stateVar) => {
        const requestOptions = {
            method: 'GET',
            headers: { 'x-access-token': localStorage.getItem('token') }
        };
        try {
            fetch(domain + '/api/v1/' + item, requestOptions)
                .then(r => r.json())
                .then(data => {
                    console.log("Fetched data for", item, data);
                    if (item.indexOf('mule') !== -1)
                        setState(prev => ({ ...prev, [stateVar]: data.mule }));
                    else if (item.indexOf('jenkins') !== -1)
                        setState(prev => ({ ...prev, [stateVar]: data.jenkins }));
                    else if (item.indexOf('sonarqube') !== -1)
                        setState(prev => ({ ...prev, [stateVar]: data.sonarqube }));
                    else if (item === 'awsresource')
                        setState(prev => ({ ...prev, [stateVar]: Array.isArray(data) ? data : (data.resources || data.awsResource || []) }));
                    else if (item === 'azresource')
                        setState(prev => ({ ...prev, [stateVar]: Array.isArray(data) ? data : (data.resources || data.azResource || []) }));
                    else
                        setState(prev => ({ ...prev, [stateVar]: data }));
                });
        } catch (e) { console.log("Fetching Error", e); }
    };

    const deleteResource = async (id, item, stateVar, extra = {}) => {
        const isConfirmed = await confirm('Do you confirm your choice?');
        setState(prev => ({ ...prev, confirm: false }));
        if (isConfirmed) {
            const requestOptions = {
                method: 'DELETE',
                headers: { 'x-access-token': localStorage.getItem('token') || '', 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, ...extra })
            };
            let url = (item === 'resource')
                ? (domain + `/api/v1/` + item)
                : domain + `/api/v1/` + item + `?id=${id}`;
            const token = localStorage.getItem('token');
            console.log(`[AccessManagement] DELETE ${item.toUpperCase()} → ${url}`, { id, ...extra });
            console.log(`[AccessManagement] DELETE token:`, token ? `${token.slice(0, 20)}...` : 'NULL — not found in localStorage');
            try {
                fetch(url, requestOptions)
                    .then(r => {
                        console.log(`[AccessManagement] DELETE ${item.toUpperCase()} response status:`, r.status);
                        return r.json();
                    })
                    .then(data => {
                        console.log(`[AccessManagement] DELETE ${item.toUpperCase()} response body:`, data);
                        getResource(item, stateVar);
                    });
            } catch (e) { console.log(`[AccessManagement] DELETE ${item.toUpperCase()} ERROR:`, e); }
        }
    };

    const postResource = (postObj, url, stateVar) => {
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
            body: JSON.stringify(postObj)
        };
        fetch(domain + '/api/v1/' + url, requestOptions)
            .then(r => r.json())
            .then(() => getResource(url, stateVar));
    };

    const putResource = (postObj, url, stateVar, itemId) => {
        console.log('[putResource] PUT', `${domain}/api/v1/${url}/${itemId}`, postObj);
        const requestOptions = {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
            body: JSON.stringify(postObj)
        };
        fetch(`${domain}/api/v1/${url}/${itemId}`, requestOptions)
            .then(r => r.json())
            .then(data => { console.log('[putResource] response:', data); getResource(url, stateVar); })
            .catch(e => console.error('[putResource] error:', e));
    };

    const Validation = (resourceLists, validationState) => {
        resourceLists.map((item) => {
            const isValid = state[item.stateVal] !== '' && state[item.stateVal] !== undefined;
            let validObj = state[validationState];
            validObj[item.stateVal] = isValid;
            setState(prev => ({ ...prev, [validationState]: validObj }));
        });
        if (Object.values(state[validationState]).every(i => i)) return true;
    };

    const handleOpen = (integration) => {
        setState(prev => ({
            ...prev,
            showModal: true,
            activeModalType: integration.modalType,
            selectedIntegration: integration,
            activePage: integration.activePage
        }));
    };

    const handleClose = () => {
        setIsEditMode(false);
        setEditItem(null);
        setState(prev => ({
            ...prev,
            showModal: false,
            activeTab: "",
            activeModalType: "",
            selectedIntegration: null,
            activePage: ''
        }));
    };

    const handleDetailsModalClose = () => {
        setState(prev => ({ ...prev, showDetailsModal: false, selectedIntegration: null }));
    };

    // Open the expanded details modal (replaces old activePage content)
    const handleExpandedModalOpen = (integration) => {
        setState(prev => ({
            ...prev,
            showExpandedModal: true,
            expandedIntegration: integration
        }));
    };

    const handleExpandedModalClose = () => {
        setState(prev => ({ ...prev, showExpandedModal: false, expandedIntegration: null }));
    };

    const openMuleSettings = () => setState(prev => ({ ...prev, showMuleSettings: true }));
    const closeMuleSettings = () => {
        handleClose();
        setState(prev => ({ ...prev, showMuleSettings: false }));
    };

    const changeOrg = (org) => setState(prev => ({ ...prev, newOrg: org }));
    const changeServiceAccount = (acct) => setState(prev => ({ ...prev, newGkeAcc: acct }));

    const MASKED_SECRET = '••••••••';

    const getEditFields = (config, integrationId) => {
        switch (integrationId) {
            case 'github':     return { gitUsername: config.gitUsername || '', gitPat: MASKED_SECRET, gitRepo: config.gitRepo || '' };
            case 'gitlab':     return { gitlabUsername: config.gitlabUsername || '', gitlabPat: MASKED_SECRET };
            case 'jenkins':    return { jenkinsServer: config.jenkinsServer || '', jenkinsToken: MASKED_SECRET, jenkinsUsername: config.jenkinsUsername || '' };
            case 'bitbucket':  return { bitbucketUsername: config.bitbucketUsername || '', bitbucketPat: MASKED_SECRET, bitbucketWorkspace: config.bitbucketWorkspace || '', bitbucketProject: config.bitbucketProject || '' };
            case 'sonarqube':  return { sonarqubeServer: config.sonarServer || '', sonarUsername: config.sonarUsername || '', sonarPassword: MASKED_SECRET };
            case 'confluence': return { confluenceEmail: config.confluenceEmail || '', confluencePat: MASKED_SECRET, confluenceBaseUrl: config.confluenceBaseUrl || '' };
            case 'elk':        return { elkIP: config.elkIP || '', elkUsername: config.elkUsername || '', elkPassword: MASKED_SECRET };
            case 'grafana':    return { grafanaServer: config.grafanaServer || '', grafanaToken: MASKED_SECRET };
            case 'aws':        return { awsaccesskey: MASKED_SECRET, awsaccesskeyid: config.awsaccesskeyid || '', awsaccountid: config.awsaccountid || '' };
            case 'azure':      return { username: config.azpusername || '', azusername: config.azusername || '', azpassword: MASKED_SECRET, aztenantid: config.aztenantid || '', azDevopsPat: MASKED_SECRET, organization: config.organization || '' };
            case 'anypoint':   return { muleOrganization: config.organization || '' };
            case 'mule':       return { muleClientId: config.muleClientId || '', muleClientPassword: MASKED_SECRET, connAppClientId: config.connAppClientId || '', connAppClientSecret: MASKED_SECRET, qaUsername: config.qaUsername || '', qaPassword: config.qaPassword ? MASKED_SECRET : '', prodUsername: config.prodUsername || '', prodPassword: config.prodPassword ? MASKED_SECRET : '', eeMavenUsername: config.eeMavenUsername || '', eeMavenPassword: config.eeMavenPassword ? MASKED_SECRET : '', newOrg: config.muleOrg || '' };
            default: return {};
        }
    };

    const stripMasked = (payload) => {
        const result = {};
        Object.entries(payload).forEach(([key, val]) => {
            if (val !== MASKED_SECRET) result[key] = val;
        });
        return result;
    };

    const openEditModal = (config, integration) => {
        const fields = getEditFields(config, integration.id);
        console.log('[openEditModal] integration:', integration.id, '| fields:', fields, '| config._id:', config._id);
        setEditItem({ ...config, resourceType: integration.resourceType });
        setIsEditMode(true);
        setState(prev => ({
            ...prev,
            ...fields,
            showModal: true,
            showExpandedModal: false,
            expandedIntegration: null,
            activeModalType: integration.modalType,
            selectedIntegration: integration,
            activePage: integration.activePage,
        }));
    };

    // ==========================================
    // SUBMIT FUNCTIONS
    // ==========================================

    const submitResource = async (resource) => {
        const map = {
            github: submitRepo, jenkins: submitJenkins, bitbucket: createBitBucket,
            grafana: createGrafana, aws: createAWS, azure: submit, mule: submitMule,
            muleorg: submitMuleOrg, vmec2: createVmEc2, flexgateway: createFlexGateway,
            registerflex: registerFlexGateway, addgkeaccount: addGkeAccount,
            sonarqube: submitSonarqube, gkeCluster: addGkeCluster,
            azaccount: submitAzureAccount, awsAccount: submitAwsAccount,
            gitlab: submitGitlab, elk: submitElk, confluence: submitConfluence,
            azKeyVault: submitNewAzureVault, standards: submitStandard
        };
        if (map[resource]) map[resource]();
    };

    const submitRepo = async () => {
        console.log('[submitRepo] isEditMode:', isEditMode, '| editItem._id:', editItem?._id);
        if (await Validation(githubList, 'githubValidation')) {
            const _isEdit = isEditMode; const _id = editItem?._id;
            handleClose();
            const payload = stripMasked({ gitUsername: state.gitUsername, gitPat: state.gitPat, gitRepo: state.gitRepo });
            console.log('[submitRepo] calling', _isEdit && _id ? 'PUT' : 'POST', '| payload:', payload);
            _isEdit && _id ? putResource(payload, 'repo', 'github', _id) : postResource(payload, 'repo', 'github');
        } else { console.warn('[submitRepo] Validation failed'); }
    };

    const submitGitlab = async () => {
        console.log('[submitGitlab] isEditMode:', isEditMode, '| editItem._id:', editItem?._id);
        if (await Validation(gitlabList, 'gitlabValidation')) {
            const _isEdit = isEditMode; const _id = editItem?._id;
            handleClose();
            const payload = stripMasked({ gitlabUsername: state.gitlabUsername, gitlabPat: state.gitlabPat });
            console.log('[submitGitlab] calling', _isEdit && _id ? 'PUT' : 'POST');
            _isEdit && _id ? putResource(payload, 'gitlab', 'gitlab', _id) : postResource(payload, 'gitlab', 'gitlab');
        } else { console.warn('[submitGitlab] Validation failed'); }
    };

    const submitAzureAccount = async () => {
        console.log('[submitAzureAccount] isEditMode:', isEditMode, '| editItem._id:', editItem?._id);
        if (await Validation(azureAccountList, 'azureValidation')) {
            const _isEdit = isEditMode; const _id = editItem?._id;
            handleClose();
            const payload = stripMasked({ azpusername: state.username, azppassword: null, azusername: state.azusername, azpassword: state.azpassword, aztenantid: state.aztenantid, azDevopsPat: state.azDevopsPat, organization: state.organization });
            console.log('[submitAzureAccount] calling', _isEdit && _id ? 'PUT' : 'POST');
            _isEdit && _id ? putResource(payload, 'azaccount', 'azaccount', _id) : postResource(payload, 'azaccount', 'azaccount');
        } else { console.warn('[submitAzureAccount] Validation failed'); }
    };

    const submitAwsAccount = async () => {
        console.log('[submitAwsAccount] isEditMode:', isEditMode, '| editItem._id:', editItem?._id);
        if (await Validation(awsAccountList, 'awsValidation')) {
            const _isEdit = isEditMode; const _id = editItem?._id;
            handleClose();
            setState(prev => ({ ...prev, awsResourceCreating: true }));
            const payload = stripMasked({ awsaccesskey: state.awsaccesskey, awsaccesskeyid: state.awsaccesskeyid, awsaccountid: state.awsaccountid });
            console.log('[submitAwsAccount] calling', _isEdit && _id ? 'PUT' : 'POST');
            _isEdit && _id ? putResource(payload, 'awsaccount', 'awsaccount', _id) : postResource(payload, 'awsaccount', 'awsaccount');
        } else { console.warn('[submitAwsAccount] Validation failed'); }
    };

    const createBitBucket = async () => {
        console.log('[createBitBucket] isEditMode:', isEditMode, '| editItem._id:', editItem?._id);
        if (await Validation(bitbucketList, 'bitbucketValidation')) {
            const _isEdit = isEditMode; const _id = editItem?._id;
            handleClose();
            const payload = stripMasked({ bitbucketUsername: state.bitbucketUsername, bitbucketPat: state.bitbucketPat, bitbucketWorkspace: state.bitbucketWorkspace, bitbucketProject: state.bitbucketProject });
            console.log('[createBitBucket] calling', _isEdit && _id ? 'PUT' : 'POST');
            _isEdit && _id ? putResource(payload, 'bitbucket', 'bitbucket', _id) : postResource(payload, 'bitbucket', 'bitbucket');
        } else { console.warn('[createBitBucket] Validation failed'); }
    };

    const createVmEc2 = async () => {
        if (await Validation(VMEC2List, 'vmEc2Validation')) {
            handleClose();
            postResource({ accessKeyId: state.awsaccesskeyid, keyName: state.vmKeyName }, 'vm/ec2', 'vmEc2');
        }
    };

    const createFlexGateway = async () => {
        if (await Validation(flexGatewayList, 'flexGatewayValidation')) {
            handleClose();
            postResource({
                ec2Id: state.instanceId, envId: state.flexEnvId,
                orgId: state.mule.filter(m => m.muleOrg === state.flexOrgId)[0].muleOrgId,
                type: state.flexType, name: state.flexName
            }, 'flex', 'flexGateways');
        }
    };

    const registerFlexGateway = async () => {
        if (await Validation(registerFlexGatewayList, 'registerFlexGatewayValidation')) {
            handleClose();
            let routes = [{ rules: [] }];
            for (let i = 0; i <= state.apiPathLength; i++) {
                routes[0].rules.push({ path: i > 0 ? state['apiPath' + i] : state['apiPath'] });
            }
            postResource({
                flexGateWayId: state.flexGateways.filter(f => f.name === state.flexGateWayId)[0]._id,
                appName: state.flexAppName, appUrl: state.flexAppUrl,
                apiPath: state.flexAppPath, appInstance: state.flexAppInstance,
                name: state.flexAppName, routes,
                policies: [
                    state.basicAuth ? {
                        policyRef: { name: "http-basic-authentication-flex" },
                        config: { username: state.basicAuthUsername, password: state.basicAuthPassword }
                    } : {},
                    state.rateLimiting ? {
                        policyRef: { name: "rate-limiting-flex" },
                        config: {
                            exposeHeaders: true,
                            rateLimits: [{ maximumRequests: state.flexMaxRequest, timePeriodInMilliseconds: state.flexTimePeriod }],
                            keySelector: "#[attributes.queryParams['identifier']]"
                        }
                    } : {}
                ]
            }, 'flex/register', 'registeredFlexGateways');
        }
    };

    const submitJenkins = async () => {
        console.log('[submitJenkins] isEditMode:', isEditMode, '| editItem._id:', editItem?._id);
        if (await Validation(jenkinsList, 'jenkinsValidation')) {
            const _isEdit = isEditMode; const _id = editItem?._id;
            handleClose();
            const payload = stripMasked({ jenkinsServer: state.jenkinsServer, jenkinsToken: state.jenkinsToken, jenkinsUsername: state.jenkinsUsername });
            console.log('[submitJenkins] calling', _isEdit && _id ? 'PUT' : 'POST');
            _isEdit && _id ? putResource(payload, 'jenkins', 'jenkins', _id) : postResource(payload, 'jenkins', 'jenkins');
        } else { console.warn('[submitJenkins] Validation failed'); }
    };

    const submitElk = async () => {
        console.log('[submitElk] isEditMode:', isEditMode, '| editItem._id:', editItem?._id);
        if (await Validation(elkList, 'elkValidation')) {
            const _isEdit = isEditMode; const _id = editItem?._id;
            handleClose();
            const payload = stripMasked({ elkIP: state.elkIP, elkUsername: state.elkUsername, elkPassword: state.elkPassword });
            console.log('[submitElk] calling', _isEdit && _id ? 'PUT' : 'POST');
            _isEdit && _id ? putResource(payload, 'elk', 'elk', _id) : postResource(payload, 'elk', 'elk');
        } else { console.warn('[submitElk] Validation failed'); }
    };

    const submitConfluence = async () => {
        console.log('[submitConfluence] isEditMode:', isEditMode, '| editItem._id:', editItem?._id);
        if (await Validation(confluenceList, 'confluenceValidation')) {
            const _isEdit = isEditMode; const _id = editItem?._id;
            handleClose();
            const payload = stripMasked({ confluenceEmail: state.confluenceEmail, confluencePat: state.confluencePat, confluenceBaseUrl: state.confluenceBaseUrl });
            console.log('[submitConfluence] calling', _isEdit && _id ? 'PUT' : 'POST');
            _isEdit && _id ? putResource(payload, 'confluence', 'confluence', _id) : postResource(payload, 'confluence', 'confluence');
        } else { console.warn('[submitConfluence] Validation failed'); }
    };

    const submitStandard = async () => {
        console.log('[submitStandard] isEditMode:', isEditMode, '| editItem._id:', editItem?._id);
        if (await Validation(standardsList, 'standardsValidation')) {
            const _isEdit = isEditMode; const _id = editItem?._id;
            handleClose();
            const payload = { name: state.standardName, type: state.standardType, content: state.standardContent };
            console.log('[submitStandard] calling', _isEdit && _id ? 'PUT' : 'POST');
            _isEdit && _id ? putResource(payload, 'standards', 'standards', _id) : postResource(payload, 'standards', 'standards');
        } else { console.warn('[submitStandard] Validation failed'); }
    };

    const createAzureVault = (resource) => {
        setState(prev => ({
            ...prev,
            azusername: resource.azusername,
            showModal: true, activeModalType: 'azKeyVault',
            activePage: 'Azure Key Vault', activeTab: 'azKeyVault'
        }));
    };

    const submitNewAzureVault = async () => {
        if (await Validation(azureVaultList, 'azureVaultValidation')) {
            handleClose();
            console.log('[submitNewAzureVault] payload:', { keyVaultName: state.keyVaultName, subscriptionId: state.subscriptionId, subscriptionName: state.subscriptionName, azusername: state.azusername });
            postResource({
                keyVaultName:        state.keyVaultName,
                subscriptionId:      state.subscriptionId,
                subscriptionName:    state.subscriptionName,
                tenantId:            state.tenantId,
                serviceprincipalid:  state.serviceprincipalid,
                serviceprincipalkey: state.serviceprincipalkey,
                azusername:          state.azusername,
            }, 'azurevault', 'azureVault');
        }
    };

    const createGrafana = async () => {
        console.log('[createGrafana] isEditMode:', isEditMode, '| editItem._id:', editItem?._id);
        if (await Validation(grafanaList, 'grafanaValidation')) {
            const _isEdit = isEditMode; const _id = editItem?._id;
            handleClose();
            const payload = stripMasked({ grafanaServer: state.grafanaServer, grafanaToken: state.grafanaToken });
            console.log('[createGrafana] calling', _isEdit && _id ? 'PUT' : 'POST');
            _isEdit && _id ? putResource(payload, 'grafana', 'grafana', _id) : postResource(payload, 'grafana', 'grafana');
        } else { console.warn('[createGrafana] Validation failed'); }
    };

    const submitMule = async () => {
        await Validation(muleEnvList, 'muleValidation');
        if (state.muleValidation.muleClientId && state.muleValidation.muleClientPassword &&
            state.muleValidation.connAppClientId && state.muleValidation.connAppClientSecret) {
            const _isEdit = isEditMode; const _id = editItem?._id;
            handleClose();
            const postObj = stripMasked({
                muleClientId: state.muleClientId, muleClientSecret: state.muleClientPassword,
                connAppClientId: state.connAppClientId, connAppClientSecret: state.connAppClientSecret,
                qaUsername: state.qaUsername, qaPassword: state.qaPassword,
                prodUsername: state.prodUsername, prodPassword: state.prodPassword,
                eeMavenUsername: state.eeMavenUsername, eeMavenPassword: state.eeMavenPassword,
                muleOrg: state.newOrg
            });
            console.log('[submitMule] calling', _isEdit && _id ? 'PUT' : 'POST', '| id:', _id);
            try {
                const method = _isEdit && _id ? 'PUT' : 'POST';
                const url = _isEdit && _id ? `${domain}/api/v1/mule/${_id}` : `${domain}/api/v1/mule`;
                fetch(url, {
                    method,
                    headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
                    body: JSON.stringify(postObj)
                })
                    .then(r => {
                        if (!r.ok) setState(prev => ({ ...prev, validationError: "Error saving mule settings, please verify your credentials and organization details", validationFailed: true }));
                        return r.json();
                    })
                    .then(() => getResource('mule', 'mule'));
            } catch (e) {
                setState(prev => ({ ...prev, validationError: e, validationFailed: true }));
            }
        }
    };

    const submitMuleOrg = async () => {
        if (await Validation(orgList, 'muleOrgValidation')) {
            handleClose();
            postResource({ organization: state.muleOrganization }, 'mule/org', 'muleOrg');
        }
    };

    const submitSonarqube = async () => {
        console.log('[submitSonarqube] isEditMode:', isEditMode, '| editItem._id:', editItem?._id);
        if (await Validation(sonarList, 'sonarValidation')) {
            const _isEdit = isEditMode; const _id = editItem?._id;
            handleClose();
            const payload = stripMasked({ sonarServer: state.sonarqubeServer, sonarUsername: state.sonarUsername, sonarPassword: state.sonarPassword });
            console.log('[submitSonarqube] calling', _isEdit && _id ? 'PUT' : 'POST');
            _isEdit && _id ? putResource(payload, 'sonarqube', 'sonarqube', _id) : postResource(payload, 'sonarqube', 'sonarqube');
        } else { console.warn('[submitSonarqube] Validation failed'); }
    };

    const submit = async () => {
        if (await Validation(azureList, 'azureValidation')) {
            handleClose();
            setState(prev => ({ ...prev, azureResourceCreating: true }));
            setTimeout(() => setState(prev => ({ ...prev, azureResourceCreating: false })), 600000);
            postResource({
                azpusername: state.username, azppassword: null,
                azusername: state.azusername, azpassword: state.azpassword,
                aztenantid: state.aztenantid, azDevopsPat: state.azDevopsPat,
                organization: state.organization, location: "eastus",
                createCluster: JSON.parse(state.createCluster),
                resource_group_name: (state.aksresourcegroup + (Math.random() + 1).toString(36).substring(7) + 'rg').replace(/[^a-zA-Z ]/g, ""),
                containerRepository: (state.aksresourcegroup + (Math.random() + 1).toString(36).substring(7) + 'containerRepo').replace(/[^a-zA-Z ]/g, ""),
                azure_container_registry_name: (state.aksresourcegroup + (Math.random() + 1).toString(36).substring(7) + 'containerreg').replace(/[^a-zA-Z ]/g, ""),
                aks_cluster_name: (state.aksresourcegroup + (Math.random() + 1).toString(36).substring(7) + 'cluster').replace(/[^a-zA-Z ]/g, "")
            }, 'azresource', 'resource');
        }
    };

    const createAWS = async () => {
        if (await Validation(awsList, 'awsValidation')) {
            handleClose();
            setState(prev => ({ ...prev, awsResourceCreating: true }));
            postResource({
                awsaccesskey: state.awsaccesskey, awsaccesskeyid: state.awsaccesskeyid,
                location: state.awsregion, awscluster: state.awscluster,
                eks_environment: state.eks_environment, eks_vpc_cidr: state.eks_vpc_cidr,
                eks_node_instance_type: state.eks_node_instance_type,
                eks_node_min_size: state.eks_node_min_size, eks_node_max_size: state.eks_node_max_size,
                eks_node_desired_size: state.eks_node_desired_size, eks_node_volume_size: state.eks_node_volume_size,
            }, 'awsresource', 'awsresources');
        }
    };

    const addGkeAccount = async () => {
        handleClose();
        setState(prev => ({ ...prev, gkeResourceCreating: true }));
        setTimeout(() => setState(prev => ({ ...prev, gkeResourceCreating: false })), 600000);
        const postObj = {
            serviceAccEmail: file.replace(/(\r\n|\n|\r)/gm, "").split('"client_email": "')[1].split('",')[0],
            serviceAccKeyFile: file
        };
        fetch(domain + '/api/v1/gkeresource', {
            method: 'POST',
            headers: { 'content-type': 'application/json', 'x-access-token': localStorage.getItem('token') },
            body: JSON.stringify(postObj)
        })
            .then(r => r.json())
            .then(() => { setState(prev => ({ ...prev, gkeResourceCreating: false })); getResource('gkeresource', 'gkeresource'); });
    };

    const addGkeCluster = async () => {
        handleClose();
        setState(prev => ({ ...prev, gkeResourceCreating: true }));
        setTimeout(() => setState(prev => ({ ...prev, gkeResourceCreating: false })), 600000);
        fetch(domain + '/api/v1/gkecluster', {
            method: 'POST',
            headers: { 'content-type': 'application/json', 'x-access-token': localStorage.getItem('token') },
            body: JSON.stringify({ serviceAccEmail: state.newGkeAcc, gkeCluster: state.gkeClusterName, gkeRegion: state.gkeRegion })
        })
            .then(r => r.json())
            .then(() => { setState(prev => ({ ...prev, gkeResourceCreating: false })); getResource('gkecluster', 'gkecluster'); });
    };

    // Triggered from within Anypoint details – opens mule settings sub-modal
    const createMuleSettings = (org) => {
        changeOrg(org);
        setState(prev => ({ ...prev, activePage: 'Mule' }));
        // Open the addnewresource modal scoped to mule
        setState(prev => ({ ...prev, showModal: true, activeModalType: 'mule', activePage: 'Mule' }));
    };

    const createGkeCluster = (account) => {
        changeServiceAccount(account);
        setState(prev => ({ ...prev, showModal: true, activeModalType: 'gkeCluster', activePage: 'GkeCluster' }));
    };

    const createAzureCluster = (resource) => {
        setState(prev => ({
            ...prev,
            username: resource.azpusername, password: resource.azppassword,
            azusername: resource.azusername, azpassword: resource.azpassword,
            aztenantid: resource.aztenantid, azDevopsPat: resource.azDevopsPat,
            organization: resource.organization,
            showModal: true, activeModalType: 'azCluster', activePage: 'Azure', activeTab: 'azCluster'
        }));
    };

    const createAwsCluster = (resource) => {
        setState(prev => ({
            ...prev,
            awsaccesskey: resource.awsaccesskey, awsregion: resource.awsregion,
            awsaccesskeyid: resource.awsaccesskeyid,
            eks_environment: 'production', eks_vpc_cidr: '10.2.0.0/16',
            eks_node_instance_type: 't3.medium', eks_node_min_size: 1,
            eks_node_max_size: 5, eks_node_desired_size: 1, eks_node_volume_size: 50,
            showModal: true, activeModalType: 'aws', activePage: 'AWS', activeTab: 'awsCluster'
        }));
    };

    const setActiveMuleContent = (val, muleOrg) => {
        setState(prev => ({ ...prev, selectedMuleOrg: muleOrg.organization }));
        state.mule.forEach(mule => {
            if (mule.muleOrg === muleOrg.organization) {
                setState(prev => ({
                    ...prev,
                    selectedMuleUsername: mule.muleUsername,
                    selectedMulePassword: mule.mulePassword,
                    selectedMuleOrgId: mule.muleOrgId,
                    muleSettingsId: mule._id
                }));
            }
        });
        setState(prev => ({ ...prev, activeMuleContent: val }));
        openMuleSettings();
    };

    const createVpc = async () => { /* VPC creation logic unchanged */ };
    const createVpn = async () => { /* VPN creation logic unchanged */ };
    const createMuleEnvironment = async () => { /* Env creation logic unchanged */ };

    const createRTF = async () => {
        let requestList = rtfRequestList.slice();
        requestList.push({ label: 'Target Cluster', stateVal: 'targetCluster' });
        requestList.push({ stateVal: 'deployTargetEnv' });
        if (await Validation(requestList, 'rtfValidation')) {
            closeMuleSettings();
            setState(prev => ({ ...prev, rtfResourceCreating: true }));
            fetch(domain + '/api/v1/mule/rtf', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-access-token': localStorage.getItem('token') },
                body: JSON.stringify({
                    muleSettingsId: state.muleSettingsId, organizationID: state.selectedMuleOrg,
                    mulePassword: state.selectedMulePassword, rtfRegion: state.rtfRegion,
                    rtfVendor: state.rtfVendor, rtfLicenseKey: state.rtfLicenseKey,
                    rtfName: state.rtfName, rtfOrg: state.selectedMuleOrgId,
                    muleUsername: state.selectedMuleUsername,
                    deployTargetEnv: state.deployTargetEnv, targetCluster: state.targetCluster
                })
            })
                .then(r => r.json())
                .then(() => {
                    setState(prev => ({ ...prev, rtfResourceCreating: false }));
                    closeMuleSettings();
                    getResource('mule/rtf', 'rtfData');
                });
        }
    };

    // ==========================================
    // USEEFFECT
    // ==========================================

    const loadData = () => {
        setRefreshing(true);
        const resources = [
            ['repo', 'github'], ['elk', 'elk'], ['gitlab', 'gitlab'],
            ['azresource', 'resource'], ['awsresource', 'awsresources'],
            ['gkeresource', 'gkeresource'], ['jenkins', 'jenkins'],
            ['mule', 'mule'], ['mule/org', 'muleOrg'], ['mule/rtf', 'rtfData'],
            ['bitbucket', 'bitbucket'], ['vm/ec2', 'ec2List'],
            ['flex', 'flexGateways'], ['register/flex', 'registeredGateways'],
            ['sonarqube', 'sonarqube'], ['gkecluster', 'gkecluster'],
            ['awsaccount', 'awsaccount'], ['azaccount', 'azaccount'],
            ['confluence', 'confluence'],
            ['azurevault', 'azureVault'],
            ['standards', 'standards'],
        ];
        resources.forEach(([endpoint, key]) => getResource(endpoint, key));
        setTimeout(() => setRefreshing(false), 1200);
    };

    useEffect(() => {
        loadData();

        const socket = socketIOClient(ENDPOINT, { transports: ['websocket'] });
        socket.on("create-resource", data => {
            if (data.status === "done") {
                getResource('repo', 'github');
                getResource('mule/org', 'muleOrg');
            }
        });
    }, []);

    // ==========================================
    // EXPANDED DETAILS MODAL CONTENT
    // (replaces old switch/case per-page content)
    // ==========================================

    const renderExpandedContent = (integration) => {
        if (!integration) return null;

        switch (integration.id) {

            case 'anypoint':
                return (
                    <div className="anypoint-expanded">
                        {state.muleOrg && state.muleOrg.length ? state.muleOrg.map((muleOrgItem) => (
                            <div key={muleOrgItem._id} className="anypoint-org-card">
                                {/* Org Header */}
                                <div className="anypoint-org-header">
                                    <span className="anypoint-org-name">{muleOrgItem.organization}</span>
                                </div>

                                {/* Mule Settings */}
                                {Array.isArray(state.mule) && state.mule.filter(m => m.muleOrg === muleOrgItem.organization).map((muleItem) => (
                                    <div key={muleItem._id} className="anypoint-mule-row">
                                        <span className="anypoint-mule-icon">⚡</span>
                                        <div className="anypoint-mule-info" onClick={() => gotoPage(`/anypoint/${muleOrgItem._id}/${muleItem._id}`)}>
                                            <span className="anypoint-mule-label">
                                                {muleItem.connAppClientId ? 'Connected App' : 'Mule Client'}
                                            </span>
                                            <span className="anypoint-mule-sub">Mule Settings</span>
                                        </div>
                                        <button
                                            className="anypoint-mule-edit"
                                            title="Edit"
                                            onClick={(e) => { e.stopPropagation(); openEditModal(muleItem, { id: 'mule', resourceType: 'mule', modalType: 'mule', activePage: 'Mule' }); }}
                                        >✏️</button>
                                        <button
                                            className="anypoint-mule-delete"
                                            title="Delete"
                                            onClick={(e) => { e.stopPropagation(); deleteResource(muleItem._id, 'mule', 'mule'); }}
                                        >✕</button>
                                        <span className="anypoint-arrow" onClick={() => gotoPage(`/anypoint/${muleOrgItem._id}/${muleItem._id}`)}>→</span>
                                    </div>
                                ))}

                                {/* RTF instances */}
                                {Array.isArray(state.rtfData) && state.rtfData.filter(r => r.rtfOrg === muleOrgItem.organization).map((rtf, i) => (
                                    <div key={i} className="anypoint-rtf-row">
                                        <span className="anypoint-mule-icon">🔁</span>
                                        <span>{rtf.rtfName}</span>
                                        <span className="anypoint-badge">RTF</span>
                                    </div>
                                ))}

                                {/* Flex Gateways */}
                                {Array.isArray(state.flexGateways) && state.flexGateways.filter(f => f.orgId === muleOrgItem._id).map((flex, i) => (
                                    <div key={i} className="anypoint-flex-row">
                                        <span className="anypoint-mule-icon">🔀</span>
                                        <span>{flex.name}</span>
                                        <span className="anypoint-badge">Flex</span>
                                    </div>
                                ))}

                                {/* Action buttons */}
                                <div className="anypoint-org-actions">
                                    <button className="anypoint-action-btn"
                                        onClick={() => { handleExpandedModalClose(); createMuleSettings(muleOrgItem.organization); }}>
                                        + Mule Settings
                                    </button>
                                    <button className="anypoint-action-btn"
                                        onClick={() => setActiveMuleContent('RTF', muleOrgItem)}>
                                        + RTF
                                    </button>
                                    <button className="anypoint-action-btn"
                                        onClick={() => { handleExpandedModalClose(); handleOpen({ modalType: 'flexgateway', activePage: 'Flex Gateway' }); }}>
                                        + Flex Gateway
                                    </button>
                                </div>
                            </div>
                        )) : <NoSettingsFound message='No organization found' />}
                    </div>
                );

            case 'azure':
                return (
                    <div className="settings-account-list">
                        {state.azaccount && state.azaccount.length ? state.azaccount.map((resource, index) => (
                            <div key={resource._id || index} className="account-card">
                                {/* Account Header */}
                                <div className="account-card-header">
                                    <div className="account-avatar">AZ</div>
                                    <div className="account-info">
                                        <span className="account-title">{resource.azpusername}</span>
                                        <span className="account-subtitle">{resource.organization}</span>
                                    </div>
                                    <button className="btn btn-sm btn-outline-secondary ms-auto"
                                        title="Edit"
                                        onClick={(e) => { e.stopPropagation(); openEditModal(resource, { id: 'azure', resourceType: 'azaccount', modalType: 'azaccount', activePage: 'Azure' }); }}>
                                        ✏️ Edit
                                    </button>
                                </div>

                                {/* Account Details */}
                                <div className="account-details">
                                    <div className="detail-row">
                                        <span className="detail-label">DevOps Org</span>
                                        <span className="detail-value">{resource.organization}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Username</span>
                                        <span className="detail-value">{resource.azpusername}</span>
                                    </div>
                                </div>

                                {/* AKS Clusters */}
                                <div className="cluster-section">
                                    <div className="cluster-section-header">
                                        <span className="cluster-section-title">AKS Clusters</span>
                                        <button className="create-az-btn btn btn-primary btn-sm"
                                            onClick={() => { handleExpandedModalClose(); createAzureCluster(resource); }}>
                                            + Add Cluster
                                        </button>
                                    </div>
                                    {Array.isArray(state.resource) && state.resource.filter(r => r.azusername === resource.azusername).length
                                        ? state.resource.filter(r => r.azusername === resource.azusername).map((azr, i) => (
                                            <div key={i} className="cluster-item">
                                                <span className="cluster-name">{azr.aks_cluster_name}</span>
                                                <span className="cluster-group">{azr.resource_group_name}</span>
                                            </div>
                                        ))
                                        : <p className="no-data-text">No AKS clusters yet</p>
                                    }
                                </div>

                                {/* Key Vaults */}
                                <div className="cluster-section">
                                    <div className="cluster-section-header">
                                        <span className="cluster-section-title">Key Vaults</span>
                                        <button className="create-az-btn btn btn-primary btn-sm"
                                            onClick={() => { handleExpandedModalClose(); createAzureVault(resource); }}>
                                            + Add Key Vault
                                        </button>
                                    </div>
                                    {(Array.isArray(state.azureVault) ? state.azureVault : []).filter(v => v.azusername === resource.azusername).length
                                        ? (Array.isArray(state.azureVault) ? state.azureVault : []).filter(v => v.azusername === resource.azusername).map((vault, i) => (
                                            <div key={i} className="cluster-item">
                                                <span className="cluster-name">{vault.keyVaultName}</span>
                                                <span className="cluster-group">{vault.subscriptionName} · {vault.subscriptionId}</span>
                                            </div>
                                        ))
                                        : <p className="no-data-text">No key vaults yet</p>
                                    }
                                </div>
                            </div>
                        )) : <NoSettingsFound message='No Azure accounts found' />}
                    </div>
                );
            case 'aws':
                return (
                    <div className="settings-account-list">
                        {state.awsaccount && state.awsaccount.length ? state.awsaccount.map((resource, index) => (
                            <div key={resource._id || index} className="account-card">
                                {/* Account Header */}
                                <div className="account-card-header">
                                    <div className="account-avatar aws">AWS</div>
                                    <div className="account-info">
                                        <span className="account-title">{resource.awsaccesskeyid}</span>
                                        <span className="account-subtitle">Amazon Web Services</span>
                                    </div>
                                </div>

                                {/* Account Details */}
                                <div className="account-details">
                                    <div className="detail-row">
                                        <span className="detail-label">Access Key ID</span>
                                        <span className="detail-value">{resource.awsaccesskeyid}</span>
                                    </div>
                                    {/* <div className="detail-row">
                                        <span className="detail-label">Access Key</span>
                                        <span className="detail-value">{resource.awsaccesskey}</span>
                                    </div> */}
                                </div>

                                {/* Clusters under this account */}
                                {Array.isArray(state.awsresources) && state.awsresources.filter(c => c.awsaccesskeyid === resource.awsaccesskeyid).length > 0 && (
                                    <div className="cluster-list">
                                        <div className="cluster-list-label">EKS Clusters</div>
                                        {state.awsresources.filter(c => c.awsaccesskeyid === resource.awsaccesskeyid).map((cluster, ci) => (
                                            <div key={cluster._id || ci} className="cluster-row">
                                                <div className="cluster-row-info">
                                                    <span className="cluster-name">🖥 {cluster.awscluster}</span>
                                                    <span className="cluster-region">{cluster.location}</span>
                                                </div>
                                                <button
                                                    className="btn btn-sm btn-danger cluster-delete-btn"
                                                    onClick={() => deleteResource(cluster._id, 'awsresource', 'awsresources', { awsaccesskeyid: cluster.awsaccesskeyid })}>
                                                    🗑 Delete
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="account-actions">
                                    <button className="btn btn-link action-link"
                                        onClick={() => gotoPage(`/aws/${resource.awsaccesskeyid}`)}>
                                        Go to AWS Settings →
                                    </button>
                                    <button className="btn btn-sm btn-outline-secondary"
                                        title="Edit"
                                        onClick={(e) => { e.stopPropagation(); openEditModal(resource, { id: 'aws', resourceType: 'awsaccount', modalType: 'awsAccount', activePage: 'AWS' }); }}>
                                        ✏️ Edit
                                    </button>
                                    <button className="create-az-btn btn btn-primary btn-sm"
                                        onClick={() => { handleExpandedModalClose(); createAwsCluster(resource); }}>
                                        + Add AWS Cluster
                                    </button>
                                </div>
                            </div>
                        )) : <NoSettingsFound message='No AWS accounts found' />}
                    </div>
                );
            case 'gke':
                return (
                    <div>
                        <Button className="create-az-btn mb-3"
                            onClick={() => { handleExpandedModalClose(); handleOpen(integrationDefinitions.find(i => i.id === 'gke')); }}>
                            + Add GKE Account
                        </Button>
                        {state.gkeresource && state.gkeresource.length ? state.gkeresource.map((gkeAccount, index) => (
                            <div key={index} className="mb-3">
                                <div className="org-panel">
                                    <span className="orgname">{gkeAccount.serviceAccEmail}</span>
                                </div>
                                <div className="p-3 border">
                                    <TableBodyforGke type="gke" data={state.gkecluster} gkeAccount={gkeAccount}
                                        deleteResource={deleteResource} url='gkecluster' stateVar='gkecluster' />
                                    <button className="create-az-btn btn btn-primary settings-add mt-2"
                                        onClick={() => { handleExpandedModalClose(); createGkeCluster(gkeAccount.serviceAccEmail); }}>
                                        + Add Cluster
                                    </button>
                                </div>
                            </div>
                        )) : <NoSettingsFound message='No GKE accounts found' />}
                    </div>
                );

            case 'standards':
                return (
                    <div className="configurations-list">
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                            <button className="btn btn-sm btn-primary"
                                onClick={() => setState(prev => ({ ...prev, showModal: true, activeModalType: 'standards', activePage: 'Standards' }))}>
                                + Add More
                            </button>
                        </div>
                        {(state.standards || []).length > 0 ? (state.standards || []).map((std, idx) => (
                            <div key={idx} className="config-item mb-3 p-3 border rounded">
                                <div className="config-header d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="mb-1">{std.name}</h5>
                                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: 'rgba(0,201,141,0.12)', color: '#00c98d', fontWeight: 600 }}>
                                            {std.type}
                                        </span>
                                    </div>
                                    <button className="btn btn-sm btn-danger"
                                        onClick={() => deleteResource(std._id, 'standards', 'standards')}>
                                        Delete
                                    </button>
                                </div>
                                {std.content && (
                                    <div className="config-details mt-2">
                                        <p style={{ fontSize: 12, color: '#666', maxHeight: 60, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {std.content.slice(0, 200)}{std.content.length > 200 ? '…' : ''}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )) : <NoSettingsFound message="No compliance standards saved yet" />}
                    </div>
                );

            default:
                return renderGenericDetails(integration);
        }
    };

    const renderGenericDetails = (integration) => {
        const configs = state[integration.stateKey] || [];
        return (
            <div className="configurations-list">
                {configs.length > 0 ? configs.map((config, index) => (
                    <div key={index} className="config-item mb-3 p-3 border rounded">
                        <div className="config-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Configuration #{index + 1}</h5>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="btn btn-sm btn-outline-primary"
                                    onClick={() => openEditModal(config, integration)}>
                                    ✏️ Edit
                                </button>
                                <button className="btn btn-sm btn-danger"
                                    onClick={() => deleteResource(config._id, integration.resourceType, integration.stateKey)}>
                                    Delete
                                </button>
                            </div>
                        </div>
                        <div className="config-details mt-2">
                            {integration.id === 'github' && (<><p><strong>Username:</strong> {config.gitUsername}</p><p><strong>Repository:</strong> {config.gitRepo || 'N/A'}</p></>)}
                            {integration.id === 'gitlab' && <p><strong>Username:</strong> {config.gitlabUsername}</p>}
                            {integration.id === 'jenkins' && (<><p><strong>Server:</strong> {config.jenkinsServer}</p><p><strong>Username:</strong> {config.jenkinsUsername}</p></>)}
                            {integration.id === 'bitbucket' && (<><p><strong>Username:</strong> {config.bitbucketUsername}</p><p><strong>Workspace:</strong> {config.bitbucketWorkspace}</p></>)}
                            {integration.id === 'sonarqube' && (<><p><strong>Server:</strong> {config.sonarServer}</p><p><strong>Username:</strong> {config.sonarUsername}</p></>)}
                            {integration.id === 'confluence' && (<><p><strong>Email:</strong> {config.confluenceEmail}</p><p><strong>Base URL:</strong> {config.confluenceBaseUrl}</p></>)}
                            {integration.id === 'elk' && (<><p><strong>ELK IP:</strong> {config.elkIP}</p><p><strong>Username:</strong> {config.elkUsername}</p></>)}
                        </div>
                    </div>
                )) : <NoSettingsFound message={`No ${integration.name} configurations found`} />}
            </div>
        );
    };

    // ==========================================
    // INTEGRATION CARD GRID
    // ==========================================

    const getIntegrationStatus = (integration) => {
        if (integration.comingSoon) return 'coming-soon';
        const data = state[integration.stateKey];
        return data && data.length > 0 ? 'configured' : 'not-installed';
    };

    const getIntegrationCount = (integration) => {
        const data = state[integration.stateKey];
        return data ? data.length : 0;
    };

    const renderIntegrationsGrid = () => (
        <div className="integrations-page">
            <div className="integrations-header">
                <PageHeader title="Connected Apps" subtitle="Connect your development tools and workflows with INTELOG" />
                <div className="ca-header-row">
                    <div className="ca-static-hint">
                        <span className="ca-static-hint-icon">💡</span>
                        <span className="ca-static-hint-text">
                            Each app card has an <strong>ⓘ</strong> icon next to its status badge — click it for step-by-step instructions to generate the credentials, tokens, or API keys required to connect that app.
                        </span>
                    </div>
                    <button
                        className={`ca-refresh-btn${refreshing ? " ca-refresh-btn--spinning" : ""}`}
                        onClick={loadData}
                        disabled={refreshing}
                        title="Refresh configurations"
                    >
                        ↻
                    </button>
                </div>
            </div>

            <div className="integrations-container">
                {(state.azureResourceCreating || state.awsResourceCreating || state.gkeResourceCreating || state.rtfResourceCreating) && (
                    <div className="progress-label-wrap">Resource creating in progress...</div>
                )}
                {state.muleOrgNotDelete && <div className="progress-label-wrap">Delete the Mule Settings first.</div>}
                <div className={'error-container ' + state.validationFailed}><label>{state.validationError}</label></div>

                <div className="integrations-grid">
                    {integrationDefinitions.map((integration) => {
                        const status = getIntegrationStatus(integration);
                        const count = getIntegrationCount(integration);
                        const isConfigured = status === 'configured';
                        const isComingSoon = status === 'coming-soon';

                        return (
                            <div key={integration.id} className={`integration-card ${status} ${integration.id}`}>
                                <div className={`card-icon-wrapper ${integration.id}`}>
                                    {integration.id === 'anypoint' ? (
                                        // Show only the MuleSoft "M" mark (crop out the wordmark below it).
                                        <span className="anypoint-mark" style={{ backgroundImage: `url(${mulesoftLogo})` }} />
                                    ) : BRAND_LOGOS[integration.id] ? (
                                        <img
                                            className="icon-logo"
                                            src={BRAND_LOGOS[integration.id]}
                                            alt={integration.name}
                                            onError={(e) => { e.target.style.display = 'none'; const em = e.target.nextElementSibling; if (em) em.style.display = 'inline'; }}
                                        />
                                    ) : null}
                                    <span className="icon" style={BRAND_LOGOS[integration.id] ? { display: 'none' } : undefined}>{integration.icon}</span>
                                </div>
                                <div className="card-content">
                                    <div className="card-header-section">
                                        <h3>
                                            {integration.name}
                                            {isConfigured && count > 0 && <span className="count-badge">{count}</span>}
                                        </h3>
                                        <div className="card-header-right">
                                            <span className={`status-badge ${status}`}>
                                                {isConfigured ? 'Configured' : isComingSoon ? 'Coming Soon' : 'Not Installed'}
                                            </span>
                                            {integration.guide && (
                                                <button
                                                    className={`ca-guide-btn${openGuide?.id === integration.id ? ' active' : ''}`}
                                                    title="How to configure"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        if (openGuide?.id === integration.id) { setOpenGuide(null); return; }
                                                        const r = e.currentTarget.getBoundingClientRect();
                                                        const spaceBelow = window.innerHeight - r.bottom - 16;
                                                        const spaceAbove = r.top - 16;
                                                        const maxHeight = Math.min(420, Math.max(spaceBelow, spaceAbove, 200));
                                                        const showAbove = spaceBelow < 220 && spaceAbove > spaceBelow;
                                                        const top = showAbove
                                                            ? Math.max(8, r.top - maxHeight - 8)
                                                            : r.bottom + 8;
                                                        setOpenGuide({ id: integration.id, top, right: window.innerWidth - r.right, maxHeight });
                                                    }}
                                                >ⓘ</button>
                                            )}
                                        </div>
                                    </div>
                                    <p className="card-description">{integration.description}</p>
                                    <div className="card-actions">
                                        {!isComingSoon ? (
                                            <div className="card-action-btns">
                                                {isConfigured ? (
                                                    <>
                                                        <button
                                                            className="action-btn action-btn--outline"
                                                            onClick={(e) => { e.stopPropagation(); handleExpandedModalOpen(integration); }}
                                                        >
                                                            View Details
                                                        </button>
                                                        <button
                                                            className="action-btn action-btn--primary"
                                                            onClick={(e) => { e.stopPropagation(); handleOpen(integration); }}
                                                        >
                                                            + Add More
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        className="action-btn action-btn--primary action-btn--full"
                                                        onClick={(e) => { e.stopPropagation(); handleOpen(integration); }}
                                                    >
                                                        Configure →
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="coming-soon-text">This integration will be available soon</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    // ==========================================
    // MAIN RENDER
    // ==========================================

    const activeGuideIntegration = openGuide ? integrationDefinitions.find(d => d.id === openGuide.id) : null;

    return (
        <div className="root">
            <CustomHeader />
            <main className="content">
                {renderIntegrationsGrid()}

                {/* Guide popover — rendered fixed so it escapes card overflow:hidden */}
                {openGuide && activeGuideIntegration && (
                    <>
                        <div
                            className="ca-guide-popover"
                            style={{ top: openGuide.top, right: openGuide.right, maxHeight: openGuide.maxHeight }}
                        >
                            <div className="ca-guide-popover-header">
                                <span>{activeGuideIntegration.guide.title}</span>
                                <button onClick={() => setOpenGuide(null)}>✕</button>
                            </div>
                            <ol className="ca-guide-steps">
                                {activeGuideIntegration.guide.steps.map((step, idx) => (
                                    <li key={idx}>{step}</li>
                                ))}
                            </ol>
                        </div>
                    </>
                )}

                {/* Configure / Add Modal */}
                <Modal show={state.showModal} onHide={handleClose} className="resourceModal">
                    <AddNewResource
                        onChange={onChange}
                        submitResource={submitResource}
                        state={state}
                        handleChange={handleChange}
                        activePage={state.activePage}
                        isEditMode={isEditMode}
                    />
                </Modal>

                {/* Expanded Details Modal (Anypoint, Azure, AWS, GKE have rich sub-content) */}
                <Modal
                    show={state.showExpandedModal}
                    onHide={handleExpandedModalClose}
                    className="resourceModal integration-details-modal"
                    size="lg"
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {state.expandedIntegration && (
                                <><span className="icon-title">{state.expandedIntegration.icon}</span> {state.expandedIntegration.name} Settings</>
                            )}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {renderExpandedContent(state.expandedIntegration)}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleExpandedModalClose}>Close</Button>
                        {state.expandedIntegration && !['anypoint', 'azure', 'aws', 'gke'].includes(state.expandedIntegration.id) && (
                            <Button className="create-az-btn" onClick={() => { handleExpandedModalClose(); handleOpen(state.expandedIntegration); }}>
                                + Add New Configuration
                            </Button>
                        )}
                    </Modal.Footer>
                </Modal>

                {/* Mule Sub-Settings Modal (VPC, VPN, RTF, Environment) */}
                <Modal show={state.showMuleSettings} onHide={closeMuleSettings} className="muleResourceModal">
                    <AddActiveMuleContent
                        onChange={onChange}
                        state={state}
                        createVpc={createVpc}
                        createRTF={createRTF}
                        createVpn={createVpn}
                        createMuleEnvironment={createMuleEnvironment}
                    />
                </Modal>
            </main>
        </div>
    );
}

export default Accessmanagement;